// Wunli.com Print Studio · Central API service
// All admin/customer interactions through this single service layer.
import axios from 'axios';

const API_BASE = (process.env.REACT_APP_BACKEND_URL || 'https://print.wunli.com').replace(/\/$/, '') + '/api';

const client = axios.create({
  baseURL: API_BASE,
  timeout: 30000,
  headers: { 'Content-Type': 'application/json' }
});

client.interceptors.request.use(cfg => {
  const token = localStorage.getItem('admin_token') || localStorage.getItem('user_token');
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

client.interceptors.response.use(
  r => r,
  err => {
    if (err.response?.status === 401) {
      localStorage.removeItem('admin_token');
      localStorage.removeItem('user_token');
      if (!window.location.pathname.includes('/login')) {
        window.location.href = '/admin/login';
      }
    }
    return Promise.reject(err);
  }
);

const safe = async (p) => {
  try { const r = await p; return r.data; }
  catch (e) { console.error('[api]', e?.response?.status, e?.response?.data || e.message); return null; }
};

// ── Auth ──────────────────────────────────────────────────────────
export const authApi = {
  adminLogin: (email, password, phoneCode = '123456') =>
    safe(client.post('/auth/admin/login', { email, password, phoneCode })),
  userLogin: (email, password) =>
    safe(client.post('/auth/login', { email, password })),
  register: (data) =>
    safe(client.post('/auth/register', data)),
};

// ── Admin Users (CRUD) ────────────────────────────────────────────
export const usersApi = {
  list: (page = 1, pageSize = 50, userType = null) => {
    const params = { page, page_size: pageSize };
    if (userType) params.user_type = userType;
    return safe(client.get('/admin/users', { params }));
  },
  create: (data) => safe(client.post('/admin/users', data)),
  update: (id, data) => safe(client.put(`/admin/users/${id}`, data)),
  remove: (id) => safe(client.delete(`/admin/users/${id}`)),
};

// ── Resellers ─────────────────────────────────────────────────────
export const resellersApi = {
  list: () => usersApi.list(1, 200, 'reseller'),
  create: (data) => usersApi.create({ ...data, userType: 'reseller' }),
  update: (id, data) => usersApi.update(id, data),
  remove: (id) => usersApi.remove(id),
  upgrade: (data) => safe(client.post('/reseller/upgrade', data)),
  commissions: () => safe(client.get('/reseller/commissions')),
};

// ── Cards ─────────────────────────────────────────────────────────
export const cardsApi = {
  list: () => safe(client.get('/cards')),
  get: (id) => safe(client.get(`/cards/${id}`)),
  create: (data) => safe(client.post('/cards', data)),
  // WPS-017 · legacy endpoint now routes to preview-mode for safety
  generatePdf: (id) => safe(client.post(`/cards/${id}/generate-pdf`)),
  // WPS-017 · explicit preview endpoint (client safe · watermarked low-res)
  previewPdf: (id) => safe(client.post(`/cards/${id}/preview-pdf`)),
  // WPS-025 · Canvas Editor persistence
  saveCanvas: (id, canvasLayout) => safe(client.patch(`/cards/${id}/canvas`, { canvasLayout })),
  setMode: (id, designMode) => safe(client.patch(`/cards/${id}/design-mode`, { designMode })),
  // WPS-025 v2 · Canvas image upload
  uploadCanvasImage: (id, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return safe(client.post(`/cards/${id}/canvas-image`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }));
  },
};

// ── Templates ─────────────────────────────────────────────────────
export const templatesApi = {
  list: () => safe(client.get('/templates')),
  create: (data) => safe(client.post('/admin/templates', data)),
  update: (id, data) => safe(client.put(`/admin/templates/${id}`, data)),
  remove: (id) => safe(client.delete(`/admin/templates/${id}`)),
};

// ── Products ──────────────────────────────────────────────────────
export const productsApi = {
  list: () => safe(client.get('/products')),
  create: (data) => safe(client.post('/admin/products', data)),
  update: (id, data) => safe(client.put(`/admin/products/${id}`, data)),
  remove: (id) => safe(client.delete(`/admin/products/${id}`)),
};

// ── Orders ────────────────────────────────────────────────────────
export const ordersApi = {
  create: (data) => safe(client.post('/orders', data)),
  listMine: () => safe(client.get('/me/orders')),
  myDashboard: () => safe(client.get('/me/dashboard')),
  listAll: () => safe(client.get('/admin/orders')),
  // WPS-001 atelier workflow
  listByStatus: (statuses) => {
    const params = Array.isArray(statuses) ? { statuses: statuses.join(',') } : { status: statuses };
    return safe(client.get('/admin/orders', { params }));
  },
  transition: (id, toStatus, notes = null, trackingNumber = null) =>
    safe(client.post(`/admin/orders/${id}/transition`, {
      to_status: toStatus,
      notes,
      trackingNumber,
    })),
  queueCounts: () => safe(client.get('/admin/orders/queue-counts')),
  // WPS-017 · admin-only print-grade PDF (requires status=approved)
  printPdf: (orderId) => safe(client.post(`/admin/orders/${orderId}/print-pdf`)),
};

// WPS-017 · cards.previewPdf (client safe · watermarked)
// Note: cardsApi.generatePdf legacy still works (backend routes to preview mode for safety).

// ── Payments ──────────────────────────────────────────────────────
export const paymentsApi = {
  process: (data) => safe(client.post('/payments/process', data)),
  listMethods: () => safe(client.get('/admin/payment-methods')),
  updateMethod: (id, data) => safe(client.put(`/admin/payment-methods/${id}`, data)),
};

// ── MLM Settings ──────────────────────────────────────────────────
export const mlmApi = {
  get: () => safe(client.get('/admin/mlm-settings')),
  update: (data) => safe(client.put('/admin/mlm-settings', data)),
};

// ── Discount Codes ────────────────────────────────────────────────
export const discountsApi = {
  list: () => safe(client.get('/admin/discount-codes')),
  create: (data) => safe(client.post('/admin/discount-codes', data)),
  remove: (id) => safe(client.delete(`/admin/discount-codes/${id}`)),
};

// ── Chat ──────────────────────────────────────────────────────────
export const chatApi = {
  createSession: (customerId, resellerId = null) =>
    safe(client.post('/chat/sessions', { customer_id: customerId, reseller_id: resellerId })),
  sendMessage: (sessionId, message) =>
    safe(client.post('/chat/messages', { session_id: sessionId, message })),
  listSessions: () => safe(client.get('/admin/chat-sessions')),
  listMessages: (sessionId) => safe(client.get(`/admin/chat-sessions/${sessionId}/messages`)),
};

// ── Support Team CRUD ─────────────────────────────────────────────
export const supportApi = {
  list: () => safe(client.get('/admin/support-team')),
  create: (data) => safe(client.post('/admin/support-team', data)),
  update: (id, data) => safe(client.put(`/admin/support-team/${id}`, data)),
  remove: (id) => safe(client.delete(`/admin/support-team/${id}`)),
};

// ── AI Settings + Training ────────────────────────────────────────
export const aiApi = {
  getSettings: () => safe(client.get('/admin/ai-settings')),
  updateSettings: (data) => safe(client.put('/admin/ai-settings', data)),
  listTraining: () => safe(client.get('/admin/ai-training')),
  addTraining: (data) => safe(client.post('/admin/ai-training', data)),
  removeTraining: (id) => safe(client.delete(`/admin/ai-training/${id}`)),
};

// ── Add-ons CRUD ──────────────────────────────────────────────────
export const addonsApi = {
  list: () => safe(client.get('/admin/add-ons')),
  create: (data) => safe(client.post('/admin/add-ons', data)),
  update: (id, data) => safe(client.put(`/admin/add-ons/${id}`, data)),
  remove: (id) => safe(client.delete(`/admin/add-ons/${id}`)),
};

// ── Partners CRUD (B2B Partner Program) ───────────────────────────
export const partnersApi = {
  list: () => safe(client.get('/admin/partners')),
  create: (data) => safe(client.post('/admin/partners', data)),
  update: (id, data) => safe(client.put(`/admin/partners/${id}`, data)),
  remove: (id) => safe(client.delete(`/admin/partners/${id}`)),
};

// ── Stripe + Phone Verification ───────────────────────────────────
export const stripeApi = {
  status: () => safe(client.get('/admin/stripe/status')),
  createIntent: (amount, orderId) =>
    safe(client.post('/payments/stripe/create-intent', { amount, orderId })),
};

export const phoneApi = {
  sendCode: (phone) => safe(client.post('/auth/send-phone-code', { phone })),
  verifyCode: (phone, code) => safe(client.post('/auth/verify-phone-code', { phone, code })),
};

// ── Analytics ─────────────────────────────────────────────────────
export const adminApi = {
  analytics: () => safe(client.get('/admin/analytics')),
  users: (page, ps, ut) => usersApi.list(page, ps, ut),
  orders: () => ordersApi.listAll(),
  settings: () => safe(client.get('/admin/settings')),
  updateSettings: (data) => safe(client.put('/admin/settings', data)),
};

// ── Uploads ───────────────────────────────────────────────────────
export const uploadApi = {
  logo: async (file) => {
    const fd = new FormData();
    fd.append('file', file);
    try {
      const r = await client.post('/upload/logo', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      return r.data;
    } catch (e) { console.error('[upload]', e); return null; }
  }
};

// ── Static defaults ───────────────────────────────────────────────
export const STATIC_FONTS = [
  { id: 1, name: 'Inter', family: 'Inter, sans-serif', category: 'Sans-serif' },
  { id: 2, name: 'Poppins', family: 'Poppins, sans-serif', category: 'Sans-serif' },
  { id: 3, name: 'Open Sans', family: 'Open Sans, sans-serif', category: 'Sans-serif' },
  { id: 4, name: 'Montserrat', family: 'Montserrat, sans-serif', category: 'Sans-serif' },
  { id: 5, name: 'Playfair Display', family: 'Playfair Display, serif', category: 'Serif' }
];

export const DEFAULT_MLM_STRUCTURE = {
  commissionStructure: { level1: 25, level2: 10, level3: 5, level4: 2, level5: 1 },
  achievementLevels: [
    { name: 'Bronze', minMonthlyVolume: 0, maxMonthlyVolume: 1000, bonusPercentage: 0 },
    { name: 'Silver', minMonthlyVolume: 1001, maxMonthlyVolume: 5000, bonusPercentage: 15 },
    { name: 'Gold', minMonthlyVolume: 5001, maxMonthlyVolume: 15000, bonusPercentage: 20 },
    { name: 'Platinum', minMonthlyVolume: 15001, maxMonthlyVolume: null, bonusPercentage: 25 }
  ]
};

// ── Assets Library (WPS-009) ──────────────────────────────────────
export const assetsApi = {
  list: (params = {}) => safe(client.get('/assets', { params })),
  adminList: () => safe(client.get('/admin/assets')),
  create: (data) => safe(client.post('/admin/assets', data)),
  update: (id, data) => safe(client.put(`/admin/assets/${id}`, data)),
  remove: (id) => safe(client.delete(`/admin/assets/${id}`)),
  upload: (file, type = 'icon', category = null) => {
    const form = new FormData();
    form.append('file', file);
    const params = { type };
    if (category) params.category = category;
    return safe(client.post('/admin/upload/asset', form, {
      params,
      headers: { 'Content-Type': 'multipart/form-data' }
    }));
  },
};

// ── Font Pairings (WPS-018) ──────────────────────────────────────
export const fontPairingsApi = {
  list: (params = {}) => safe(client.get('/font-pairings', { params })),
  create: (data) => safe(client.post('/admin/font-pairings', data)),
  update: (id, data) => safe(client.put(`/admin/font-pairings/${id}`, data)),
  remove: (id) => safe(client.delete(`/admin/font-pairings/${id}`)),
};

// ── Brand Pairings (WPS-015) ─────────────────────────────────────
export const pairingsApi = {
  list: (params = {}) => safe(client.get('/brand-pairings', { params })),
  create: (data) => safe(client.post('/admin/brand-pairings', data)),
  update: (id, data) => safe(client.put(`/admin/brand-pairings/${id}`, data)),
  remove: (id) => safe(client.delete(`/admin/brand-pairings/${id}`)),
};

// ── Customer Photo Upload (WPS-016) ──────────────────────────────
export const customerPhotoApi = {
  upload: (cardId, file) => {
    const form = new FormData();
    form.append('file', file);
    return safe(client.post(`/cards/${cardId}/upload-photo`, form, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }));
  },
};

// ── Wunli AI Agent (WPS-013) ─────────────────────────────────────
export const wunliAgentApi = {
  ask: (task, context) => safe(client.post('/ai/wunli-agent', { task, context })),
};

// ── WPS-019 · Customer Profile CRM ───────────────────────────────
export const profileApi = {
  // Customer self-service
  getMine: () => safe(client.get('/me/profile')),
  updateMine: (data) => safe(client.patch('/me/profile', data)),
  regenerateSlug: () => safe(client.post('/me/smart-card/regenerate-slug')),
  // Admin CRM
  adminList: (params = {}) => safe(client.get('/admin/customer-profiles', { params })),
  adminGet: (id) => safe(client.get(`/admin/customer-profiles/${id}`)),
  adminUpdate: (id, data) => safe(client.patch(`/admin/customer-profiles/${id}`, data)),
  adminSegments: () => safe(client.get('/admin/customer-profiles/segments')),
  adminSync: () => safe(client.post('/admin/customer-profiles/sync')),
  adminBackfill: () => safe(client.post('/admin/customer-profiles/backfill')),
};

// ── WPS-020 · Smart Card public (no auth) ────────────────────────
export const smartCardApi = {
  getPublic: (slug) => safe(client.get(`/public/sc/${slug}`)),
  vcardUrl: (slug) => `${API_BASE}/public/sc/${slug}/vcard`,
};

// ── WPS-023 · Marketing Automation ────────────────────────────────
export const marketingApi = {
  audiencePreview: (audience, channel = 'email') =>
    safe(client.post('/admin/marketing/audience-preview', { audience, channel })),
  listCampaigns: (params = {}) => safe(client.get('/admin/marketing/campaigns', { params })),
  getCampaign: (id) => safe(client.get(`/admin/marketing/campaigns/${id}`)),
  createCampaign: (data) => safe(client.post('/admin/marketing/campaigns', data)),
  updateCampaign: (id, data) => safe(client.patch(`/admin/marketing/campaigns/${id}`, data)),
  sendCampaign: (id) => safe(client.post(`/admin/marketing/campaigns/${id}/send`)),
  recipients: (id, status = null) =>
    safe(client.get(`/admin/marketing/campaigns/${id}/recipients`, { params: status ? { status } : {} })),
  // WPS-024 renewal
  renewalScan: () => safe(client.get('/admin/marketing/renewal-scan')),
  renewalApply: () => safe(client.post('/admin/marketing/renewal-scan/apply')),
};

// ── WPS-027 · Smart Referral Loop ─────────────────────────────────
export const referralApi = {
  track: (referrerSlug, sessionToken = null) =>
    safe(client.post('/public/referral/track', { referrerSlug, sessionToken, userAgent: navigator.userAgent })),
  convert: (sessionToken, userId) =>
    safe(client.post('/public/referral/convert', { sessionToken, userId })),
  mine: () => safe(client.get('/me/referrals')),
  leaderboard: () => safe(client.get('/admin/referrals/leaderboard')),
};

// ── WPS-028 · Wunli Showcase ──────────────────────────────────────
export const showcaseApi = {
  list: (industry = null, featuredOnly = false) =>
    safe(client.get('/public/showcase', { params: { industry, featured_only: featuredOnly } })),
  updateMyOptIn: (showcaseOptIn, showcaseIndustry = null) =>
    safe(client.patch('/me/showcase', { showcaseOptIn, showcaseIndustry })),
  adminFeature: (profileId, featured) =>
    safe(client.patch(`/admin/customer-profiles/${profileId}/feature`, { featured })),
};

// ── WPS-026 · Plans (Free + Pro) ──────────────────────────────────
export const plansApi = {
  list: () => safe(client.get('/plans')),
  upgradeIntent: () => safe(client.post('/me/plan/upgrade-intent')),
  myAnalytics: () => safe(client.get('/me/analytics')),
  adminSetPlan: (userId, tier) => safe(client.post(`/admin/users/${userId}/plan`, { tier })),
};

export default {
  authApi, adminApi, usersApi, resellersApi, cardsApi, templatesApi,
  productsApi, ordersApi, paymentsApi, mlmApi, discountsApi, chatApi,
  supportApi, aiApi, stripeApi, phoneApi, addonsApi, partnersApi, uploadApi,
  assetsApi, pairingsApi, customerPhotoApi, wunliAgentApi,
  profileApi, smartCardApi, marketingApi,
  referralApi, showcaseApi, plansApi,
  STATIC_FONTS, DEFAULT_MLM_STRUCTURE
};
